// Storage utility functions for SendItMaxx
// All localStorage operations centralized here for better maintainability

const STORAGE_PREFIX = 'senditmaxx_';

// Generic storage helpers
export const getStorageItem = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(`${STORAGE_PREFIX}${key}`);
    return item !== null ? item : defaultValue;
  } catch (error) {
    console.error('Error reading from localStorage:', error);
    return defaultValue;
  }
};

export const setStorageItem = (key, value) => {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${key}`, value);
  } catch (error) {
    console.error('Error writing to localStorage:', error);
  }
};

export const removeStorageItem = (key) => {
  try {
    localStorage.removeItem(`${STORAGE_PREFIX}${key}`);
  } catch (error) {
    console.error('Error removing from localStorage:', error);
  }
};

// User data storage
export const getUserId = () => getStorageItem('user_id');
export const setUserId = (userId) => setStorageItem('user_id', userId);

export const getUsername = () => getStorageItem('username');
export const setUsername = (username) => setStorageItem('username', username);

export const getBio = () => getStorageItem('bio');
export const setBio = (bio) => setStorageItem('bio', bio);

export const getLastUsernameChange = () => {
  const value = getStorageItem('last_username_change');
  return value ? parseInt(value) : null;
};
export const setLastUsernameChange = (timestamp) => setStorageItem('last_username_change', timestamp.toString());

// Game state storage
export const getStreak = () => {
  const value = getStorageItem('streak');
  return value ? parseInt(value) : 0;
};
export const setStreak = (streak) => setStorageItem('streak', streak.toString());

export const getScore = () => {
  const value = getStorageItem('score');
  return value ? parseInt(value) : 0;
};
export const setScore = (score) => setStorageItem('score', score.toString());

export const getLogs = () => {
  const value = getStorageItem('logs');
  return value ? JSON.parse(value) : [];
};
export const setLogs = (logs) => setStorageItem('logs', JSON.stringify(logs));

export const getDecisions = () => {
  const value = getStorageItem('decisions');
  return value ? JSON.parse(value) : [];
};
export const setDecisions = (decisions) => setStorageItem('decisions', JSON.stringify(decisions));

export const getPendingOutcomes = () => {
  const value = getStorageItem('pending_outcomes');
  return value ? JSON.parse(value) : [];
};
export const setPendingOutcomes = (pendingOutcomes) => setStorageItem('pending_outcomes', JSON.stringify(pendingOutcomes));

export const getLongestStreak = () => {
  const value = getStorageItem('longest_streak');
  return value ? parseInt(value) : 0;
};
export const setLongestStreak = (longestStreak) => setStorageItem('longest_streak', longestStreak.toString());

export const getRecoveryMode = () => {
  const value = getStorageItem('recovery_mode');
  return value ? JSON.parse(value) : false;
};
export const setRecoveryMode = (recoveryMode) => setStorageItem('recovery_mode', JSON.stringify(recoveryMode));

export const getLastCompletion = () => {
  const value = getStorageItem('last_completion');
  return value ? parseInt(value) : null;
};
export const setLastCompletion = (timestamp) => setStorageItem('last_completion', timestamp.toString());

// Challenge storage
export const getDailyChallenge = () => {
  const value = getStorageItem('daily_challenge');
  return value ? JSON.parse(value) : null;
};
export const setDailyChallenge = (challenge) => setStorageItem('daily_challenge', JSON.stringify(challenge));

export const getWeeklyChallenge = () => {
  const value = getStorageItem('weekly_challenge');
  return value ? JSON.parse(value) : null;
};
export const setWeeklyChallenge = (challenge) => setStorageItem('weekly_challenge', JSON.stringify(challenge));

export const getWeeklyCompleted = () => {
  const value = getStorageItem('weekly_completed');
  return value ? JSON.parse(value) : false;
};
export const setWeeklyCompleted = (completed) => setStorageItem('weekly_completed', JSON.stringify(completed));

export const getWeeklyStartDate = () => {
  const value = getStorageItem('weekly_start_date');
  return value ? parseInt(value) : null;
};
export const setWeeklyStartDate = (timestamp) => setStorageItem('weekly_start_date', timestamp.toString());

// Community storage
export const getUserVotes = () => {
  const value = getStorageItem('user_votes');
  return value ? JSON.parse(value) : {};
};
export const setUserVotes = (votes) => setStorageItem('user_votes', JSON.stringify(votes));

export const savePost = (postId, post) => setStorageItem(`post:${postId}`, JSON.stringify(post));
export const getPost = (postId) => {
  const value = getStorageItem(`post:${postId}`);
  return value ? JSON.parse(value) : null;
};
export const deletePost = (postId) => removeStorageItem(`post:${postId}`);

export const saveComment = (commentId, comment) => setStorageItem(`comment:${commentId}`, JSON.stringify(comment));
export const getComment = (commentId) => {
  const value = getStorageItem(`comment:${commentId}`);
  return value ? JSON.parse(value) : null;
};
export const deleteComment = (commentId) => removeStorageItem(`comment:${commentId}`);

export const getAllPosts = () => {
  const posts = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(`${STORAGE_PREFIX}post:`)) {
        const post = localStorage.getItem(key);
        if (post) {
          posts.push(JSON.parse(post));
        }
      }
    }
  } catch (error) {
    console.error('Error loading posts:', error);
  }
  return posts;
};

export const getAllComments = () => {
  const comments = {};
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(`${STORAGE_PREFIX}comment:`)) {
        const comment = localStorage.getItem(key);
        if (comment) {
          const parsedComment = JSON.parse(comment);
          if (parsedComment && parsedComment.postId) {
            if (!comments[parsedComment.postId]) {
              comments[parsedComment.postId] = [];
            }
            comments[parsedComment.postId].push(parsedComment);
          }
        }
      }
    }

    // Sort comments by timestamp (oldest first)
    Object.keys(comments).forEach(postId => {
      comments[postId].sort((a, b) => a.timestamp - b.timestamp);
    });
  } catch (error) {
    console.error('Error loading comments:', error);
  }
  return comments;
};

// Settings storage
export const getBadges = () => {
  const value = getStorageItem('badges');
  return value ? JSON.parse(value) : [];
};
export const setBadges = (badges) => setStorageItem('badges', JSON.stringify(badges));

export const getCountdownUses = () => {
  const value = getStorageItem('countdown_uses');
  return value ? parseInt(value) : 0;
};
export const setCountdownUses = (count) => setStorageItem('countdown_uses', count.toString());

export const getOnboardingComplete = () => {
  const value = getStorageItem('onboarding_complete');
  return value ? JSON.parse(value) : false;
};
export const setOnboardingComplete = (complete) => setStorageItem('onboarding_complete', JSON.stringify(complete));

export const getVisitCount = () => {
  const value = getStorageItem('visit_count');
  return value ? parseInt(value) : 0;
};
export const setVisitCount = (count) => setStorageItem('visit_count', count.toString());

export const getSoundEnabled = () => {
  const value = getStorageItem('sound_enabled');
  return value !== null ? JSON.parse(value) : true; // Default to true
};
export const setSoundEnabled = (enabled) => setStorageItem('sound_enabled', JSON.stringify(enabled));

export const getPWAPromptSeen = () => {
  const value = getStorageItem('pwa_prompt_seen');
  return value ? JSON.parse(value) : false;
};
export const setPWAPromptSeen = (seen) => setStorageItem('pwa_prompt_seen', JSON.stringify(seen));

// Notification storage
export const getLastStreakWarning = () => {
  const value = getStorageItem('last_streak_warning');
  return value ? parseInt(value) : null;
};
export const setLastStreakWarning = (timestamp) => setStorageItem('last_streak_warning', timestamp.toString());

// Challenge completion tracking (for stats)
export const getLocalChallengeCount = (type, challengeText) => {
  const value = getStorageItem(`local_challenge_count:${type}:${challengeText}`);
  return value ? parseInt(value) : 0;
};
export const setLocalChallengeCount = (type, challengeText, count) => {
  setStorageItem(`local_challenge_count:${type}:${challengeText}`, count.toString());
};
export const incrementLocalChallengeCount = (type, challengeText) => {
  const currentCount = getLocalChallengeCount(type, challengeText);
  setLocalChallengeCount(type, challengeText, currentCount + 1);
  return currentCount + 1;
};

// Swap It — one use per calendar week (keyed by ISO Monday date)
const getCurrentWeekKey = () => {
  const now = new Date();
  const day = now.getDay(); // 0=Sun, 1=Mon...
  const diff = (day === 0 ? -6 : 1 - day); // days to subtract to get Monday
  const monday = new Date(now);
  monday.setDate(now.getDate() + diff);
  return monday.toISOString().slice(0, 10); // "YYYY-MM-DD"
};

export const getSwapUsedWeek = () => getStorageItem('swap_used_week');
export const setSwapUsedWeek = () => setStorageItem('swap_used_week', getCurrentWeekKey());
export const isSwapUsedThisWeek = () => getStorageItem('swap_used_week') === getCurrentWeekKey();